import { ReactElement, useMemo } from 'react';
import styles from './Note.module.scss';
import { VdsAvatar, VdsIcon } from '@volt/volt-extended-components-react';
import { NoteActivityType, NoteModel } from '@customer/store/apis/commonModels';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import InterpolatedMessage from '@customer/components/data-display/interpolated-message/InterpolatedMessage';
import { useTranslation } from 'react-i18next';
import { systemNote } from '@cas/components/icons/icons';

type NoteProps = NoteModel;

export const Note = ({
  text,
  nt,
  createdAt,
  createdBy: { dn: displayName, sys: systemUser },
  im,
}: NoteProps): ReactElement => {
  const { showTimeAndDateFromIso } = useLocaleFormatter();
  const { t } = useTranslation();

  const displayDateTime = useMemo<string>(
    () => showTimeAndDateFromIso(createdAt),
    [createdAt, showTimeAndDateFromIso],
  );

  if (nt !== NoteActivityType.UserEntered) {
    return (
      <article className={styles.systemNote}>
        <div className={styles.systemNote__icon}>
          <VdsIcon
            svgIcon={nt === NoteActivityType.StatusChange ? systemNote : 'system'}
            color="color-icon-medium"
            size="24px"
          />
        </div>
        <div className={styles.created}>
          <span className={styles.created__when}>{displayDateTime}</span>
        </div>
        <div className={styles.systemNote__message}>
          {im ? <InterpolatedMessage interpolatedMessage={im} /> : text}
          {!systemUser ? t('NoteBy', { value: displayName }) : ''}
        </div>
      </article>
    );
  }

  return (
    <article className={styles.note}>
      <div className={styles.created}>
        <span className={styles.created__who}>{systemUser ? t('SystemUser') : displayName}</span>
        <span className={styles.created__when}>{displayDateTime}</span>
      </div>

      <VdsAvatar value={displayName} size="32px" />
      <div className={styles.message}>
        {im ? <InterpolatedMessage interpolatedMessage={im} /> : text}
      </div>
    </article>
  );
};

export default Note;
